import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { SUMMARY_LABEL_SX, SummaryField } from './SummaryPaneComponents';
import { InputSummary, MultiselectSummary, SwitchRow } from './SummaryPaneComponents';
import { usePrepingSelectors } from '../hooks';
import { useOfferSummaries } from '../../../views/Offers/queries';
import { useOfferActionsTable } from '../../../views/OfferActions/queries';

const PrepingSummary = () => {
  const prepingSelectors = usePrepingSelectors();
  const { data: offers, isLoading: loadingOffers, error: offerLoadingError } = useOfferSummaries();
  const { data: offerActions, isLoading: offerActionsLoading } = useOfferActionsTable();

  const internalPrePingsForSummary =
    prepingSelectors.internalPrePings?.filter((prePing) => {
      const type = prePing?.prePingSuppressionType;
      return type && ['days', 'hours', 'minutes'].includes(String(type).toLowerCase());
    }) || [];

  return (
    <>
      {prepingSelectors.isAnySelectorNotNullOrEmpty && (
        <>
          <Typography variant='h4'>Pre-ping Summary</Typography>
          <Grid container spacing={2} sx={{ alignItems: 'start' }}>
            <Grid size='grow' className='ppLeft'>
              <Typography variant='h5'>Internal Pre-Ping</Typography>
              <hr />
              {prepingSelectors.andOr && (
                <Grid container spacing={2} sx={{ alignItems: 'start' }}>
                  <Grid size='grow'>
                    <InputSummary
                      value={
                        prepingSelectors.andOr === 'AND' ? 'All entries must pass' : 'Any of these entries can pass'
                      }
                    />
                  </Grid>
                </Grid>
              )}
              <br />
              <div style={{ height: '400px', overflowY: 'scroll' }}>
                {internalPrePingsForSummary.map((prePing, index) => {
                  const additionalOffers = prePing.additionalOffers;
                  const additionalOfferActions = prePing.additionalOfferActions;
                  return (
                    <div key={index}>
                      <Card>
                        <CardContent>
                          <>
                            <Grid container spacing={2} sx={{ alignItems: 'start' }}>
                              <Grid size='grow'>
                                <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Type</FormLabel>
                                <InputSummary value={prePing.prePingSuppressionType} />
                              </Grid>
                              <Grid size='grow'>
                                <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Amount</FormLabel>
                                <InputSummary value={prePing.prePingSuppressionAmount} />
                              </Grid>
                            </Grid>
                            <br />
                            <Grid container spacing={2} sx={{ alignItems: 'start' }}>
                              <Grid size='grow'>
                                <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Level</FormLabel>
                                <InputSummary value={prePing.prePingSuppressionLevel} />
                              </Grid>
                              <Grid size='grow'>
                                <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Max Limit</FormLabel>
                                <InputSummary value={prePing.prePingSuppressionMaxLimit} />
                              </Grid>
                            </Grid>
                            {(additionalOffers && additionalOffers.length > 0) ||
                              (additionalOfferActions && additionalOfferActions.length > 0 && (
                                <Grid container spacing={2} sx={{ alignItems: 'start' }}>
                                  <Grid size='grow'>
                                    <FormLabel component='p' sx={SUMMARY_LABEL_SX}>
                                      Additional
                                      {additionalOffers && additionalOffers.length > 0 ? ' Offers' : ' Offer Actions'}
                                    </FormLabel>
                                    <MultiselectSummary
                                      value={
                                        additionalOffers && additionalOffers.length > 0
                                          ? additionalOffers.map((offerId) => {
                                              const foundOffer = offers?.find((offer) => offer.id === offerId);
                                              return foundOffer ? foundOffer.name : offerId;
                                            })
                                          : additionalOfferActions.map((offerActionId) => {
                                              const foundOfferAction = offerActions?.find(
                                                (offerAction) => offerAction.id === offerActionId
                                              );
                                              return foundOfferAction ? foundOfferAction.title : offerActionId;
                                            })
                                      }
                                      busy={
                                        additionalOffers && additionalOffers.length > 0
                                          ? loadingOffers
                                          : offerActionsLoading
                                      }
                                    />
                                  </Grid>
                                </Grid>
                              ))}
                          </>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </Grid>
            <Grid size='grow'>
              <Grid container spacing={2} sx={{ alignItems: 'start' }}>
                <Grid size='grow'>
                  <Typography variant='h5'>External Pre-Ping</Typography>
                </Grid>
              </Grid>
              <hr />
              <SwitchRow label='Pre-Ping Result Stored' value={prepingSelectors.prePingResultSessionCapture} />
              <Grid container spacing={2} sx={{ alignItems: 'start' }}>
                {prepingSelectors.prePingMethod && (
                  <Grid size='grow'>
                    <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Pre Ping Method</FormLabel>
                    <InputSummary value={prepingSelectors.prePingMethod} />
                  </Grid>
                )}
                {prepingSelectors.prePingHandlerId?.name && (
                  <Grid size='grow'>
                    <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Handler</FormLabel>
                    <InputSummary value={prepingSelectors.prePingHandlerId?.name} />
                  </Grid>
                )}
              </Grid>
              {prepingSelectors.prePing && (
                <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
                  <Grid size={12}>
                    <FormLabel component='p' sx={SUMMARY_LABEL_SX}>URL</FormLabel>
                    <InputSummary value={prepingSelectors.prePing} />
                  </Grid>
                </Grid>
              )}
              {prepingSelectors.prePingBody && (
                <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
                  <Grid size={12}>
                    <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Body</FormLabel>
                    <SummaryField value={prepingSelectors.prePingBody} multiline />
                  </Grid>
                </Grid>
              )}
              {prepingSelectors.prePingTimeout && (
                <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
                  <Grid size='grow'>
                    <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Timeout</FormLabel>
                    <InputSummary value={prepingSelectors.prePingTimeout} />
                  </Grid>
                </Grid>
              )}
              <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
                {prepingSelectors.prePingHeader1 && (
                  <Grid size='grow'>
                    <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Header 1</FormLabel>
                    <InputSummary value={prepingSelectors.prePingHeader1} />
                  </Grid>
                )}
                {prepingSelectors.prePingHeader2 && (
                  <Grid size='grow'>
                    <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Header 2</FormLabel>
                    <InputSummary value={prepingSelectors.prePingHeader2} />
                  </Grid>
                )}
                {prepingSelectors.prePingHeader3 && (
                  <Grid size='grow'>
                    <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Header 3</FormLabel>
                    <InputSummary value={prepingSelectors.prePingHeader3} />
                  </Grid>
                )}
              </Grid>
              {prepingSelectors.prePingCustomHttpStatus && (
                <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
                  <Grid size={12}>
                    <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Other HTTP Status</FormLabel>
                    <InputSummary value={prepingSelectors.prePingCustomHttpStatus} />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};
export default PrepingSummary;
